import { computed, ref, shallowRef, useHost, onBeforeUnmount, type ComputedRef, type ToRefs } from "vue";
import { useSharedState } from "composables/useSharedState";
import { useInheritProps } from "composables/useInheritProps";
import { closestAcrossShadowBoundaries } from "@/runtime/shared/shadow-dom";
import type { FieldProps } from "./field.types";
import { fieldClasses, fieldMessageClasses } from "./field.styles";
import type { Colors, Variants, Sizes } from "types";

/**
 * Registry mapping Field elements to their computed styling values.
 * WeakMap ensures entries are automatically GC'd when the element is removed.
 */
const fieldStyleRegistry = new WeakMap<
  Element,
  {
    finalColor: ComputedRef<Colors>;
    finalVariant: ComputedRef<Exclude<Variants, "link" | "ghost">>;
    finalSize: ComputedRef<Sizes>;
  }
>();

/**
 * Registers a Field's computed styling values.
 */
export const registerFieldStyles = (
  element: Element,
  styles: {
    finalColor: ComputedRef<Colors>;
    finalVariant: ComputedRef<Exclude<Variants, "link" | "ghost">>;
    finalSize: ComputedRef<Sizes>;
  },
): void => {
  fieldStyleRegistry.set(element, styles);
};

/**
 * Unregisters a Field's computed styling values.
 */
export const unregisterFieldStyles = (element: Element): void => {
  fieldStyleRegistry.delete(element);
};

/**
 * Gets a Field's computed styling values by element.
 */
export const getFieldStyles = (element: Element) => {
  return fieldStyleRegistry.get(element);
};

export interface UseFieldOptions {
  props: Readonly<FieldProps>;
}

const FORM_CONTROL_SELECTOR = "e-input, e-select, e-combobox, e-listbox, e-checkbox, e-radio, e-textarea, e-switch";

const checkChildDisabled = (host: Element | null): boolean => {
  if (!host) return false;

  const customElement = host.querySelector<HTMLElement>(FORM_CONTROL_SELECTOR);
  if (!customElement) return false;

  // Check disabled property
  if ("disabled" in customElement && (customElement as any).disabled === true) return true;

  // Check disabled attribute (for custom elements that use attributes)
  if (customElement.hasAttribute("disabled")) {
    return customElement.getAttribute("disabled") !== "false";
  }

  return false;
};

const focusChildControl = (host: Element | null): void => {
  if (!host) return;

  const customElement = host.querySelector<HTMLElement>(FORM_CONTROL_SELECTOR);
  if (!customElement) return;

  // For checkboxes/radios, trigger click to toggle
  if (customElement.tagName === "E-CHECKBOX" || customElement.tagName === "E-RADIO") {
    customElement.click();
  }

  // Call focus() on the custom element - it handles focusing its internal input
  if ("focus" in customElement && typeof customElement.focus === "function") {
    customElement.focus();
  }
};

const computeMessageProps = (
  state: ToRefs<FieldProps>,
  hasError: ComputedRef<boolean>,
  errorMessage: ComputedRef<string>,
) => {
  const messageText = computed(() => {
    if (hasError.value && errorMessage.value) return errorMessage.value;
    if (state.description?.value) return state.description.value;
    if (state.hint?.value) return state.hint.value;
    return "";
  });

  const messageType = computed<"error" | "description" | "hint">(() => {
    if (hasError.value) return "error";
    if (state.description?.value) return "description";
    return "hint";
  });

  return { messageText, messageType };
};

export const useField = (options: UseFieldOptions) => {
  const { props } = options;
  const host = useHost();
  const state = useSharedState(props);
  const contentRef = ref<HTMLElement | null>(null);

  const hasError = computed(() => {
    const error = state.error?.value;
    return error !== undefined && error !== false;
  });

  const isChildDisabled = computed(() => {
    if (state.disabled?.value === true) return true;
    return checkChildDisabled(host);
  });

  const errorMessage = computed(() => {
    const error = state.error?.value;
    return typeof error === "string" ? error : "";
  });

  const { messageText, messageType } = computeMessageProps(state, hasError, errorMessage);

  const classes = computed(() => fieldClasses({ orientation: state.orientation?.value ?? "vertical" }));
  const messageClasses = computed(() => fieldMessageClasses({ type: messageType.value }));

  const finalColor = computed<Colors>(() => (hasError.value ? "error" : (state.color?.value ?? "neutral")));
  const finalVariant = computed<Exclude<Variants, "link" | "ghost">>(() =>
    hasError.value ? "outline" : (state.variant?.value ?? "outline"),
  );
  const finalSize = computed<Sizes>(() => state.size?.value ?? "md");

  const handleLabelClick = () => {
    if (isChildDisabled.value) return;
    focusChildControl(host);
  };

  if (host) registerFieldStyles(host, { finalColor, finalVariant, finalSize });
  onBeforeUnmount(() => {
    if (host) unregisterFieldStyles(host);
  });

  return {
    contentRef,
    state,
    hasError,
    errorMessage,
    messageText,
    messageType,
    classes,
    messageClasses,
    finalColor,
    finalVariant,
    finalSize,
    handleLabelClick,
    isChildDisabled,
  };
};

/**
 * Finds a parent Field element and inherits color/variant/size from it
 * when the Field is in an error state.
 *
 * Used by child form components (Input, Select, etc.) to inherit Field styling.
 */
export const useFieldInheritance = (host: Element | null, state: ToRefs<any>) => {
  const cachedFieldElement = shallowRef<Element | null>(null);
  if (host) {
    cachedFieldElement.value = closestAcrossShadowBoundaries(host, "e-field");
  }

  return useInheritProps({
    parentState: computed(() => {
      const el = cachedFieldElement.value;
      if (!el) return;
      return getFieldStyles(el);
    }),
    ownState: state,
    inherit: {
      color: { default: "neutral" },
      variant: { default: "subtle" },
      size: { default: "md" },
    },
    shouldInherit: (parent) => parent?.finalColor?.value === "error",
  });
};
